import { useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  ActionBar,
  Button,
  CheckboxField,
  Field,
  FormCard,
  SectionHeader,
  StatusBadge,
  TextareaField,
  type Column,
} from "@fawkes/ui";

import { formatDateTime } from "../../i18n/format";
import { TokenRejectionList, type TokenRejection } from "../TokenRejectionList";
import { IndeelbareTabel } from "../../components/IndeelbareTabel";

// Pairing epic D: pair this backoffice with the Fawkes platform and test the connection.
//   1. Redeem — the admin pastes the platform's pairing code; the api creates the inbound service account,
//      redeems the code and keeps the answer as a PENDING pairing (the current connection keeps working).
//   2. Confirm — the admin allows the platform's addresses; the platform then proves both directions. When it
//      cannot reach us, the refused token requests show why (typically an address not allowed yet) with a
//      one-click "allow and confirm again".
//   3. Test — per feature a token mint, the platform's view of us (organization, our IP) and its call back.
// Secrets never reach the browser: the api answers with the masked last 4 only.

interface ConnectionView {
  source: "DATABASE" | "PROPERTIES";
  status: string | null;
  platformBaseUrl: string | null;
  organizationId: string | null;
  clientId: string | null;
  secretConfigured: boolean;
  secretLast4: string | null;
  grantedFeatures: string[];
  origin: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface PendingView {
  platformBaseUrl: string;
  organizationId: string;
  clientId: string;
  grantedFeatures: string[];
  egressIps: string[];
  confirmBefore: string | null;
}

interface ConfirmResult {
  confirmed: boolean;
  reason: string | null;
  detail: string | null;
  inboundRejections: TokenRejection[];
}

interface Check {
  name: string;
  status: "OK" | "FAILED" | "SKIPPED";
  code: string | null;
  detail: string | null;
}

interface TestResult {
  features: Check[];
  outbound: Check;
  organization: Check;
  inbound: Check;
  callerIpSeenByPlatform: string | null;
  inboundRejections: TokenRejection[];
}

const FEATURE_NAMES = ["FOSTER_WEBHOOKS", "ABSENCES", "NAZORG", "PUBLICATIONS", "WHATSAPP"];

function errorText(err: unknown, fallback: string): string {
  const message = (err as { message?: string } | undefined)?.message;
  return message && message.trim() ? message : fallback;
}

function statusTone(status: Check["status"]): "success" | "danger" | "neutral" {
  return status === "OK" ? "success" : status === "FAILED" ? "danger" : "neutral";
}

export function PlatformConnectionDetail({ onBack }: { onBack: () => void }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "en";
  // Relative urls: the dataProvider prepends "/api".
  const connectionQuery = useCustom<ConnectionView>({ url: "platform-connection", method: "get" });
  // 404 = nothing pending: an expected answer, not an error to notify.
  const pendingQuery = useCustom<PendingView>({
    url: "platform-connection/pairing",
    method: "get",
    errorNotification: false,
    queryOptions: { retry: false },
  });
  const { mutate, isLoading: busy } = useCustomMutation();

  const [code, setCode] = useState("");
  const [backofficeUrl, setBackofficeUrl] = useState(() => window.location.origin);
  // null = every address the platform listed (the default); otherwise the admin's selection.
  const [selectedIps, setSelectedIps] = useState<string[] | null>(null);
  const [extraIp, setExtraIp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmFailure, setConfirmFailure] = useState<ConfirmResult | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  const connection = connectionQuery.data?.data;
  const pendingData = pendingQuery.data?.data;
  const pending =
    !pendingQuery.isError && pendingData && typeof pendingData === "object" && "platformBaseUrl" in pendingData
      ? pendingData
      : null;
  const egressIps = Array.isArray(pending?.egressIps) ? pending!.egressIps : [];
  const chosenIps = selectedIps ?? egressIps;

  function refresh() {
    connectionQuery.refetch();
    pendingQuery.refetch();
  }

  function start() {
    setError(null);
    setNotice(null);
  }

  function redeem() {
    start();
    mutate(
      {
        url: "platform-connection/pairing/redeem",
        method: "post",
        values: { code: code.trim(), backofficeBaseUrl: backofficeUrl.trim() },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setCode("");
          setSelectedIps(null);
          setConfirmFailure(null);
          refresh();
        },
        onError: (err) => setError(errorText(err, t("platformKoppeling.error.generic"))),
      },
    );
  }

  function confirm(allowIps: string[]) {
    start();
    mutate(
      {
        url: "platform-connection/pairing/confirm",
        method: "post",
        values: { allowIps },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const result = res.data as unknown as ConfirmResult;
          if (result?.confirmed) {
            setConfirmFailure(null);
            setExtraIp("");
            setNotice(t("platformKoppeling.confirmed"));
            refresh();
          } else {
            setConfirmFailure(result);
          }
        },
        onError: (err) => setError(errorText(err, t("platformKoppeling.error.generic"))),
      },
    );
  }

  function allowIpsForConfirm(): string[] {
    const extra = extraIp.trim();
    return extra && !chosenIps.includes(extra) ? [...chosenIps, extra] : chosenIps;
  }

  function cancelPairing() {
    start();
    mutate(
      { url: "platform-connection/pairing", method: "delete", values: {}, successNotification: false },
      {
        onSuccess: () => {
          setConfirmFailure(null);
          refresh();
        },
        onError: (err) => setError(errorText(err, t("platformKoppeling.error.generic"))),
      },
    );
  }

  function runTest() {
    start();
    setTestResult(null);
    mutate(
      {
        url: "platform-connection/test",
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => setTestResult(res.data as unknown as TestResult),
        onError: (err) => setError(errorText(err, t("platformKoppeling.error.generic"))),
      },
    );
  }

  function unpair() {
    if (!window.confirm(t("platformKoppeling.unpair.confirm"))) return;
    start();
    mutate(
      { url: "platform-connection", method: "delete", values: {}, successNotification: false },
      {
        onSuccess: () => {
          setTestResult(null);
          setNotice(t("platformKoppeling.unpaired"));
          refresh();
        },
        onError: (err) => setError(errorText(err, t("platformKoppeling.error.generic"))),
      },
    );
  }

  function toggleIp(ip: string) {
    setSelectedIps((prev) => {
      const base = prev ?? egressIps;
      return base.includes(ip) ? base.filter((v) => v !== ip) : [...base, ip];
    });
  }

  const featureLabel = (name: string) =>
    FEATURE_NAMES.includes(name) ? t(`platformKoppeling.feature.${name}`) : name;

  const checkColumns: Column<Check>[] = [
    {
      header: t("platformKoppeling.test.column.check"),
      cell: (c) => (FEATURE_NAMES.includes(c.name) ? featureLabel(c.name) : t(`platformKoppeling.test.check.${c.name}`)),
    },
    {
      header: t("platformKoppeling.test.column.status"),
      cell: (c) => <StatusBadge tone={statusTone(c.status)}>{t(`platformKoppeling.test.status.${c.status}`)}</StatusBadge>,
    },
    {
      header: t("platformKoppeling.test.column.detail"),
      cell: (c) => [c.status === "FAILED" ? c.code : null, c.detail].filter(Boolean).join(" — "),
    },
  ];

  const connected = connection?.source === "DATABASE" && connection.status === "ACTIVE";
  const configured = connected || (connection?.source === "PROPERTIES" && connection.secretConfigured);

  return (
    <>
      <FormCard>
        <SectionHeader>{t("platformKoppeling.title")}</SectionHeader>
        <p className="fawkes-muted settings-ai-detail__breadcrumb">
          {`${t("instellingen.title")} › ${t("instellingen.context.organization")} › ${t(
            "instellingen.tile.platform.title",
          )}`}
        </p>
        <p className="fawkes-muted">{t("platformKoppeling.description")}</p>

        {connectionQuery.isLoading ? (
          <p className="fawkes-muted">{t("common.loading")}</p>
        ) : connectionQuery.isError || !connection ? (
          <p className="fawkes-error">{t("platformKoppeling.loadFailed")}</p>
        ) : (
          <>
            <div className="fawkes-detail-section">
              <StatusBadge tone={connected ? "success" : "neutral"}>
                {connected
                  ? t("platformKoppeling.status.connected")
                  : configured
                    ? t("platformKoppeling.status.legacy")
                    : t("platformKoppeling.status.none")}
              </StatusBadge>
              {connected && connection.origin ? (
                <span className="fawkes-muted"> {t(`platformKoppeling.origin.${connection.origin}`)}</span>
              ) : null}
            </div>
            {configured && (
              <dl className="fawkes-keyvalue">
                <dt>{t("platformKoppeling.field.platform")}</dt>
                <dd>{connection.platformBaseUrl ?? "—"}</dd>
                <dt>{t("platformKoppeling.field.organization")}</dt>
                <dd>{connection.organizationId ?? "—"}</dd>
                <dt>{t("platformKoppeling.field.clientId")}</dt>
                <dd>{connection.clientId ?? "—"}</dd>
                <dt>{t("platformKoppeling.field.secret")}</dt>
                <dd>{connection.secretLast4 ? `•••• ${connection.secretLast4}` : "—"}</dd>
                <dt>{t("platformKoppeling.field.features")}</dt>
                <dd>
                  {connection.grantedFeatures?.length ? (
                    <span className="fawkes-chips">
                      {connection.grantedFeatures.map((f) => (
                        <StatusBadge key={f} tone="neutral">
                          {featureLabel(f)}
                        </StatusBadge>
                      ))}
                    </span>
                  ) : (
                    "—"
                  )}
                </dd>
                {connection.updatedAt && (
                  <>
                    <dt>{t("platformKoppeling.field.updated")}</dt>
                    <dd>
                      {formatDateTime(connection.updatedAt, locale)}
                      {connection.updatedBy ? ` — ${connection.updatedBy}` : ""}
                    </dd>
                  </>
                )}
              </dl>
            )}
          </>
        )}

        {notice && (
          <p className="fawkes-success" role="status">
            {notice}
          </p>
        )}
        {error && (
          <p className="fawkes-error" role="alert">
            {error}
          </p>
        )}

        <ActionBar>
          <Button variant="secondary" onClick={onBack}>
            {t("common.back")}
          </Button>
          <Button variant="secondary" onClick={runTest} disabled={busy || !configured}>
            {t("platformKoppeling.test")}
          </Button>
          {connection?.source === "DATABASE" && (
            <Button variant="secondary" onClick={unpair} disabled={busy}>
              {t("platformKoppeling.unpair")}
            </Button>
          )}
        </ActionBar>
      </FormCard>

      {testResult && (
        <FormCard>
          <SectionHeader>{t("platformKoppeling.test.title")}</SectionHeader>
          <IndeelbareTabel tabelKey="settings-platform-connection-detail"
            columns={checkColumns}
            rows={[...(testResult.features ?? []), testResult.outbound, testResult.organization, testResult.inbound].filter(
              Boolean,
            )}
            getRowKey={(c) => c.name}
            empty="—"
          />
          {testResult.callerIpSeenByPlatform && (
            <p className="fawkes-muted">{t("platformKoppeling.test.callerIp", { args: [testResult.callerIpSeenByPlatform] })}</p>
          )}
          <p className="fui-field__label">{t("platformKoppeling.rejections.title")}</p>
          <TokenRejectionList
            rejections={Array.isArray(testResult.inboundRejections) ? testResult.inboundRejections : []}
            empty={t("platformKoppeling.rejections.none")}
          />
        </FormCard>
      )}

      <FormCard>
        {pending ? (
          <>
            <SectionHeader>{t("platformKoppeling.pending.title")}</SectionHeader>
            <p className="fawkes-muted">{t("platformKoppeling.pending.intro")}</p>
            <dl className="fawkes-keyvalue">
              <dt>{t("platformKoppeling.field.platform")}</dt>
              <dd>{pending.platformBaseUrl}</dd>
              <dt>{t("platformKoppeling.field.organization")}</dt>
              <dd>{pending.organizationId}</dd>
              <dt>{t("platformKoppeling.field.features")}</dt>
              <dd>{(pending.grantedFeatures ?? []).map(featureLabel).join(", ") || "—"}</dd>
            </dl>
            {pending.confirmBefore && (
              <p className="fawkes-muted">
                {t("platformKoppeling.pending.confirmBefore", { args: [formatDateTime(pending.confirmBefore, locale)] })}
              </p>
            )}
            <fieldset className="fawkes-fieldset">
              <legend className="fui-field__label">{t("platformKoppeling.pending.egress")}</legend>
              {egressIps.length === 0 && <p className="fawkes-muted">{t("platformKoppeling.pending.noEgress")}</p>}
              {egressIps.map((ip) => (
                <CheckboxField key={ip} label={ip} checked={chosenIps.includes(ip)} onChange={() => toggleIp(ip)} />
              ))}
            </fieldset>
            <Field
              label={t("platformKoppeling.pending.extraIp")}
              placeholder={t("serviceAccountDetailView.allowlist.add.placeholder")}
              value={extraIp}
              onChange={(e) => setExtraIp(e.target.value)}
            />
            {confirmFailure && (
              <>
                <p className="fawkes-error" role="alert">
                  {t("platformKoppeling.confirmFailed", { args: [confirmFailure.detail ?? confirmFailure.reason ?? ""] })}
                </p>
                <p className="fui-field__label">{t("platformKoppeling.rejections.title")}</p>
                <TokenRejectionList
                  rejections={Array.isArray(confirmFailure.inboundRejections) ? confirmFailure.inboundRejections : []}
                  empty={t("platformKoppeling.rejections.none")}
                  onAllowIp={(ip) => confirm([...allowIpsForConfirm().filter((v) => v !== ip), ip])}
                  allowLabel={(ip) => t("platformKoppeling.rejections.allowAndRetry", { args: [ip] })}
                  disabled={busy}
                />
              </>
            )}
            <ActionBar>
              <Button variant="secondary" onClick={cancelPairing} disabled={busy}>
                {t("platformKoppeling.pending.cancel")}
              </Button>
              <Button onClick={() => confirm(allowIpsForConfirm())} disabled={busy}>
                {t("platformKoppeling.pending.confirm")}
              </Button>
            </ActionBar>
          </>
        ) : (
          <>
            <SectionHeader>{t("platformKoppeling.pair.title")}</SectionHeader>
            <p className="fawkes-muted">{t("platformKoppeling.pair.intro")}</p>
            <div className="fawkes-form-grid">
              <TextareaField
                label={t("platformKoppeling.pair.code")}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <Field
                label={t("platformKoppeling.pair.backofficeUrl")}
                value={backofficeUrl}
                onChange={(e) => setBackofficeUrl(e.target.value)}
              />
            </div>
            <p className="fawkes-muted">{t("platformKoppeling.pair.backofficeUrl.help")}</p>
            <ActionBar>
              <Button onClick={redeem} disabled={busy || code.trim() === "" || backofficeUrl.trim() === ""}>
                {t("platformKoppeling.pair.redeem")}
              </Button>
            </ActionBar>
          </>
        )}
      </FormCard>
    </>
  );
}
